/**
 * Custom hook for tire price calculations
 * Provides a consistent calculator function across all components
 */

import { useCallback, useMemo } from "react";
import { ITireItemDetail } from "features/car_model/types";
import { PunctureProtection } from "features/mst_option/types/option";
import { CostPriceMap, MstProcFeeTireItem } from "features/mst_proc_fee/types/proc-fee";
import {
  calculateTirePrice,
  convertToTireCostSettings,
  PriceCalculationResult,
  TireCostSettings,
} from "features/mst_proc_fee/utils/priceCalculator";
import { parseNumber } from "util/helper";

/**
 * Props for useTirePriceCalculator hook
 */
interface UseTirePriceCalculatorProps {
  /** Number of tires (1, 2, or 4) */
  tireNumber: number;
  /** Procedure fees configuration */
  procFees: MstProcFeeTireItem | null | undefined;
  /** Whether disposal is checked */
  isDisposalChecked?: boolean;
  /** Whether valve is checked */
  isValveChecked?: boolean;
  /** Whether nitrogen is checked */
  isNitrogenChecked?: boolean;
  /** Whether hub protection is checked */
  isHubProtectionChecked?: boolean;
  /** Puncture compensation option (0, 2, or 4) */
  punctureCompensation?: number;
  /** Cashback period option (1 or 2) */
  cashbackPeriod?: number;
  /** Puncture protection data */
  punctureProtection?: PunctureProtection[] | null;
  /** Optional cost price map from API for special pricing */
  costPriceMap?: CostPriceMap;
  /** If true, always use standard pricing (list_price/a_price) regardless of price_setting_wheel */
  forceStandardPricing?: boolean;
}

/**
 * Custom hook that provides a memoized calculator function
 * @param props - Configuration for the calculator
 * @returns Calculator function
 */
export function useTirePriceCalculator(props: UseTirePriceCalculatorProps) {
  const {
    tireNumber,
    procFees,
    isDisposalChecked = true,
    isValveChecked = true,
    isNitrogenChecked = false,
    isHubProtectionChecked = false,
    punctureCompensation = 0,
    cashbackPeriod = 1,
    punctureProtection,
    costPriceMap,
    forceStandardPricing = false,
  } = props;

  // Memoize tire cost settings
  const tireCostSettings = useMemo<TireCostSettings>(() => {
    if (!procFees) {
      return {
        replac_labor_cost: 0,
        waste_tire_cost: 0,
        rubber_stem_valve_cost: 0,
        nitrogen_inflation_cost: 0,
        hub_rust_prevention_cost: 0,
        tire_puncture_flag: 0,
        cashback_flag: false,
        cashback_period: 1,
      };
    }

    return {
      replac_labor_cost: procFees.replac_labor_cost ?? 0,
      waste_tire_cost: isDisposalChecked ? (procFees.waste_tire_cost ?? 0) : 0,
      rubber_stem_valve_cost: isValveChecked ? (procFees.rubber_stem_valve_cost ?? 0) : 0,
      nitrogen_inflation_cost: isNitrogenChecked ? (procFees.nitrogen_inflation_cost ?? 0) : 0,
      hub_rust_prevention_cost: isHubProtectionChecked
        ? (procFees.hub_rust_prevention_cost ?? 0)
        : 0,
      tire_puncture_flag: punctureCompensation,
      cashback_flag: cashbackPeriod > 0,
      cashback_period: cashbackPeriod,
    };
  }, [
    procFees,
    isDisposalChecked,
    isValveChecked,
    isNitrogenChecked,
    isHubProtectionChecked,
    punctureCompensation,
    cashbackPeriod,
  ]);

  // Memoized calculator function
  const calculator = useCallback(
    (tire: ITireItemDetail): PriceCalculationResult =>
      calculateTirePrice(
        tire,
        tireNumber,
        tireCostSettings,
        punctureProtection,
        costPriceMap,
        forceStandardPricing,
      ),
    [tireNumber, tireCostSettings, punctureProtection, costPriceMap, forceStandardPricing],
  );

  return { calculator, tireCostSettings };
}

/**
 * Alternative hook for components using CostSettingInfo format
 */
interface UseTirePriceCalculatorWithCostSettingProps {
  /** Number of tires (1, 2, or 4) */
  tireNumber?: number;
  /** Cost setting info from state */
  costSettingInfo: {
    replac_labor_cost: string | number;
    waste_tire_cost_flag: boolean;
    waste_tire_cost: string | number;
    rubber_stem_valve_flag: boolean;
    rubber_stem_valve_cost: string | number;
    nitrogen_inflation_flag: boolean;
    nitrogen_inflation_cost: string | number;
    hub_rust_prevention_flag: boolean;
    hub_rust_prevention_cost: string | number;
    tire_puncture_flag: number;
    cashback_flag: boolean;
    cashback_period: string | number;
  };
  /** Puncture protection data */
  punctureProtection?: PunctureProtection[] | null;
  /** Optional cost price map from API for special pricing */
  costPriceMap?: CostPriceMap;
}

/**
 * Custom hook for components using CostSettingInfo format (like WheelTireProcFeeList)
 * @param props - Configuration for the calculator
 * @returns Calculator function
 */
export function useTirePriceCalculatorWithCostSetting(
  props: UseTirePriceCalculatorWithCostSettingProps,
) {
  const { tireNumber = 4, costSettingInfo, punctureProtection, costPriceMap } = props;

  // Convert cost setting info to tire cost settings
  const tireCostSettings = useMemo<TireCostSettings>(
    () => convertToTireCostSettings(costSettingInfo, parseNumber),
    [costSettingInfo],
  );

  // Memoized calculator function
  const calculator = useCallback(
    (tire: ITireItemDetail): PriceCalculationResult =>
      calculateTirePrice(tire, tireNumber, tireCostSettings, punctureProtection, costPriceMap),
    [tireNumber, tireCostSettings, punctureProtection, costPriceMap],
  );

  return { calculator, tireCostSettings };
}
